import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useEffect, useState, useRef } from 'react';
import { Spacings, Fonts, mediaQuery } from '@styles/index';
import KeyboardArrowDown from '@icon/KeyboardArrowDown';
import PropTypes from 'prop-types';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import P2 from '@base/P2/P2';
import Image from '@base/Image';
import useClickAwayListener from '@hooks/useClickAwayListener';

const SearchableDropdownWrapper = styled.div`
  position: relative;
  width: ${({ $width }) => $width};
  &.box-wrapper {
    box-shadow: none;
    border-style: solid;
    border-width: 1px;
    margin-bottom: ${({ $error }) => ($error ? Spacings.SPACING_7B : '0px')};
    border-color: ${({ $error }) =>
      $error ? 'var(--color-i-red)' : 'var(--color-divider)'};
    transition: border-color 400ms ease-in-out 0s;
    border-radius: ${Spacings.SPACING_2B};
    font-weight: ${Fonts.SEMIBOLD};
    padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_6B};
    :hover {
      border-color: var(--color-divider);
    }
    .search-dd-input {
      font-size: ${Spacings.SPACING_4B};
      font-weight: inherit;
      color: currentColor;
      background-color: inherit;
    }
    .search-dd-options {
      top: 55px;
      padding-top: 0px;
      max-height: 250px;
    }
  }
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SearchInput = styled.input`
  height: 100%;
  width: 100%;
  border: none;
  font-size: ${Fonts.LARGE};
  ${mediaQuery.uptoTablet} {
    font-size: ${Fonts.P2};
  }
  max-width: ${({ $stripLength }) => `${$stripLength}ch`};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledArrowDown = styled(KeyboardArrowDown)`
  transition: transform 400ms ease-in-out;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0deg)')};
  cursor: pointer;
  ${mediaQuery.uptoMobile} {
    margin-left: ${Spacings.SPACING_2B};
  }
`;

const CrossIconWrapper = styled.div`
  cursor: pointer;
  width: 14px;
  height: 14px;
  position: relative;
`;

const growDown = keyframes`
0% {
  transform: scaleY(0);
}
80% {
  transform: scaleY(1.1);
}
100% {
  transform: scaleY(1);
}
`;
const OptionsDropdown = styled.div`
  width: ${({ $width }) => $width};
  max-height: 444px;
  position: absolute;
  z-index: 2;
  display: ${({ $showDropDown }) => ($showDropDown ? 'block' : 'none')};
  overflow: scroll;
  top: 42px;
  left: 0%;
  padding: ${Spacings.SPACING_4B};
  background: var(--color-base-1);
  border: 1px solid var(--color-base-1);
  box-sizing: border-box;
  box-shadow: ${boxShadowGenerator({
    xOffset: Spacings.SPACING_0B,
    yOffset: Spacings.SPACING_2B,
    blur: Spacings.SPACING_4B,
    color: 'rgb(221 230 237 / 40%)'
  })};
  border-radius: ${Spacings.SPACING_1B};
  transition: display 400ms ease-in-out;
  ${mediaQuery.uptoTablet} {
    width: 100%;
    max-height: 320px;
    left: 0px;
    top: 80px;
    z-index: 1;
  }
  animation: ${growDown} 300ms ease-in-out forwards;
  transform-origin: top center;
`;

const OptionItem = styled.div`
  white-space: normal;
  color: ${({ $isSelected }) =>
    $isSelected ? 'var(--color-i-green)' : 'var(--color-text-primary)'};
  font-weight: ${({ $isSelected }) =>
    $isSelected ? Fonts.SEMIBOLD : Fonts.REGULAR};
  cursor: pointer;
`;

const ErrorMessage = styled(P2)`
  color: var(--color-i-red);
  margin-top: ${Spacings.SPACING_4B};
  position: absolute;
  left: 0;
`;

const SearchableDropdown = ({
  className,
  placeholder,
  selectedOption,
  dropdownItem,
  options,
  filterKey,
  width,
  toggleSearchDropdown,
  onValueChange,
  stripLength,
  labelKey,
  error,
  noOptionsText,
  wrapperStyle,
  errorText,
  onlySearchbar,
  crossIcon,
  clearOnAway,
  clearOnSelect
}) => {
  const [showDropDown, setShowDropDown] = useState(false);
  const [showFullList, setShowFullList] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const searchKey = filterKey || labelKey;
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const optionItemRef = useRef();
  const optionsWrapperRef = useRef();
  const wrapperRef = useRef();
  const currentSelectedValue = useRef('');
  useEffect(() => {
    setShowDropDown(false);
    setInputValue(selectedOption[labelKey] || '');
    currentSelectedValue.current = selectedOption[labelKey];
  }, [selectedOption, labelKey]);

  const renderDropdownOptions = (item, index) => {
    return (
      <OptionItem
        $isSelected={selectedOption[labelKey] === item[labelKey]}
        key={index.toString()}
        ref={index === selectedIndex ? optionItemRef : null}
        className={index === selectedIndex ? 'active' : ''}
      >
        {dropdownItem(item)}
      </OptionItem>
    );
  };

  const showDropdownOptions = () => {
    const searchString = inputValue ? inputValue.toLowerCase() : '';
    if (!options?.length) {
      return <OptionItem className="no-dd-option">{noOptionsText}</OptionItem>;
    }

    if (showFullList || !searchString) {
      return (
        <>{options.map((item, index) => renderDropdownOptions(item, index))}</>
      );
    }

    if (options[0][searchKey]) {
      const filterList = options.filter(
        (item) => item[searchKey].toLowerCase().indexOf(searchString) > -1
      );
      if (filterList.length === 0) {
        return (
          <OptionItem className="no-dd-option">{noOptionsText}</OptionItem>
        );
      }
      return (
        <>
          {filterList.map((item, index) => renderDropdownOptions(item, index))}
        </>
      );
    }
    return (
      <>{options.map((item, index) => renderDropdownOptions(item, index))}</>
    );
  };

  const onFocusHandler = () => {
    setShowDropDown(true);
    setShowFullList(true);
    toggleSearchDropdown(true);
    if (clearOnSelect) setInputValue('');
  };

  useClickAwayListener(wrapperRef, () => {
    if (clearOnAway) {
      setShowDropDown(false);
      toggleSearchDropdown(false);
      setInputValue(currentSelectedValue.current || '');
    }
  });
  const onChangeHandler = (e) => {
    const { value } = e.target;
    setShowFullList(false);
    setInputValue(value);
    onValueChange(value);
    if (value === '') {
      toggleSearchDropdown(true);
    }
  };

  const onCrossHandler = () => {
    setInputValue('');
    onValueChange('');
    setShowFullList(true);
  };

  useEffect(() => {
    if (optionItemRef.current) {
      optionsWrapperRef.current.scrollTop = optionItemRef.current.offsetTop;
    }
  }, [selectedIndex]);

  const handleKeyDown = (e) => {
    if (e.keyCode === 38) {
      if (selectedIndex === 0) {
        setSelectedIndex(options.length - 1);
      } else {
        setSelectedIndex(selectedIndex - 1);
      }
    } else if (e.keyCode === 40) {
      if (selectedIndex === options.length - 1) {
        setSelectedIndex(0);
      } else {
        setSelectedIndex(selectedIndex + 1);
      }
    }
  };

  return (
    <SearchableDropdownWrapper
      className={`${className} search-dd-wrapper ${
        wrapperStyle ? 'box-wrapper' : ''
      }`}
      $width={width}
      $error={error}
      ref={wrapperRef}
    >
      <InputWrapper
        className="search-dd-input-wrapper"
        onKeyDown={handleKeyDown}
      >
        <SearchInput
          type="text"
          className="search-dd-input"
          placeholder={placeholder}
          onFocus={onFocusHandler}
          onChange={onChangeHandler}
          value={inputValue}
          $stripLength={stripLength}
        />
        {!onlySearchbar && (
          <StyledArrowDown
            className="search-dd-icon"
            size="24px"
            $open={showDropDown}
            onClick={() => {
              setShowDropDown(!showDropDown);
              toggleSearchDropdown(!showDropDown);
              setShowFullList(true);
            }}
          />
        )}
        {crossIcon && !!inputValue && (
          <CrossIconWrapper>
            <Image
              src="cross.svg"
              alt="X"
              layout="fill"
              onClick={onCrossHandler}
            />
          </CrossIconWrapper>
        )}
      </InputWrapper>

      {!onlySearchbar && (
        <>
          {error ? <ErrorMessage>{errorText}</ErrorMessage> : null}

          <OptionsDropdown
            ref={optionsWrapperRef}
            className="search-dd-options"
            $showDropDown={showDropDown}
            $width={width}
          >
            {showDropdownOptions()}
          </OptionsDropdown>
        </>
      )}
    </SearchableDropdownWrapper>
  );
};

SearchableDropdown.propTypes = {
  className: PropTypes.string,
  placeholder: PropTypes.string,
  selectedOption: PropTypes.objectOf(PropTypes.any),
  options: PropTypes.arrayOf(PropTypes.any),
  dropdownItem: PropTypes.func,
  filterKey: PropTypes.string,
  labelKey: PropTypes.string,
  width: PropTypes.string,
  toggleSearchDropdown: PropTypes.func,
  onValueChange: PropTypes.func,
  stripLength: PropTypes.number,
  error: PropTypes.bool,
  errorText: PropTypes.string,
  noOptionsText: PropTypes.string,
  wrapperStyle: PropTypes.bool,
  onlySearchbar: PropTypes.bool,
  crossIcon: PropTypes.bool,
  clearOnAway: PropTypes.bool,
  clearOnSelect: PropTypes.bool
};

SearchableDropdown.defaultProps = {
  className: '',
  placeholder: '',
  selectedOption: {},
  options: [],
  dropdownItem: () => {},
  filterKey: '',
  labelKey: 'name',
  width: '100%',
  toggleSearchDropdown: () => {},
  onValueChange: () => {},
  stripLength: null,
  error: false,
  errorText: 'Please select an option',
  noOptionsText: 'No result found',
  wrapperStyle: false,
  onlySearchbar: false,
  crossIcon: false,
  clearOnAway: true,
  clearOnSelect: false
};

export default SearchableDropdown;
